import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { inviteAPI } from '../services/api';
import PasswordRequirements, { usePasswordRequirements } from '../components/PasswordRequirements';
import TextInput from '../components/common/TextInput';
import { 
  Mail, 
  Check, 
  X, 
  Loader2, 
  Eye, 
  EyeOff,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import '../styles/AcceptInvite.css';

function AcceptInvite({ onLogin }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const { isPasswordValid } = usePasswordRequirements();

  useEffect(() => {
    if (!token) {
      setError('No invitation token provided');
      setLoading(false);
      return;
    }
    
    const doValidate = async () => {
      try {
        const response = await inviteAPI.validate(token);
        if (response.data.valid) {
          setInviteData(response.data);
        } else {
          setError(response.data.error || 'Invalid invitation');
        }
      } catch (err) {
        if (err.response?.data?.alreadyActivated) {
          setError('This invitation has already been used. Please log in instead.');
        } else {
          setError(err.response?.data?.error || 'Failed to validate invitation');
        }
      } finally {
        setLoading(false);
      }
    };
    
    doValidate();
  }, [token]);

  const doPasswordsMatch = () => {
    if (!confirmPassword) return true;
    return password === confirmPassword;
  };

  const canSubmit = () => {
    return password && 
           confirmPassword && 
           isPasswordValid(password) && 
           doPasswordsMatch();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!canSubmit()) return;
    
    setSubmitting(true);
    setError('');

    try {
      const response = await inviteAPI.accept(token, password);
      
      // Store auth data
      localStorage.setItem('auth_token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      setSuccess(true);
      
      // Redirect to dashboard after a brief delay
      setTimeout(() => {
        onLogin(response.data.user, null);
        navigate('/', { replace: true });
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to accept invitation');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="accept-invite-page">
        <div className="accept-invite-container">
          <div className="loading-state">
            <Loader2 size={48} className="spinner" />
            <p>Validating invitation...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !inviteData) {
    return (
      <div className="accept-invite-page">
        <div className="accept-invite-container">
          <div className="error-state">
            <div className="error-icon">
              <X size={48} />
            </div>
            <h1>Invalid Invitation</h1>
            <p>{error}</p>
            <button 
              className="btn-primary" 
              onClick={() => navigate('/login')}
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="accept-invite-page">
        <div className="accept-invite-container">
          <div className="success-state">
            <div className="success-icon">
              <CheckCircle size={64} />
            </div>
            <h1>Welcome to Remidy!</h1>
            <p>Your account has been activated successfully.</p>
            <div className="loading-redirect">
              <Loader2 size={24} className="spinner" />
              <span>Redirecting to dashboard...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="accept-invite-page">
      <div className="accept-invite-container">
        <div className="accept-invite-header">
          <div className="invite-icon">
            <Mail size={48} />
          </div>
          <h1>Welcome to Remidy</h1>
          <p>
            Hi <strong>{inviteData?.firstName}</strong>! Set your password to activate your account.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="accept-invite-form">
          {error && (
            <div className="form-error">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          <div className="form-group">
            <label>Email Address</label>
            <TextInput 
              type="email" 
              value={inviteData?.email || ''} 
              disabled 
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Create Password</label>
            <div className="password-input-wrapper">
              <TextInput
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                disabled={submitting}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <div className="password-input-wrapper">
              <TextInput
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                disabled={submitting}
                className={
                  confirmPassword 
                    ? (doPasswordsMatch() ? 'input-success' : 'input-error')
                    : ''
                }
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {confirmPassword && (
              <small className={doPasswordsMatch() ? 'success-text' : 'error-text'}>
                {doPasswordsMatch() ? '✓ Passwords match' : 'Passwords do not match'}
              </small>
            )}
          </div>

          <PasswordRequirements password={password} showAlways={true} className="compact" />

          <button 
            type="submit" 
            className="btn-primary btn-large"
            disabled={submitting || !canSubmit()}
          >
            {submitting ? (
              <>
                <Loader2 size={20} className="spinner" />
                <span>Activating Account...</span>
              </>
            ) : (
              <>
                <Check size={20} />
                <span>Activate Account</span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

export default AcceptInvite;
